<script>
import dayjs from 'dayjs'

import { HumanFields } from '@constants/knack'
import dispatcherExclusionRule from '@utils/dispatcherExclusionRule'
import createStatusPoller from '@utils/statusPoller'
import { mapActions, mapGetters, createNamespacedHelpers } from 'vuex'
const { mapState: routeState } = createNamespacedHelpers('route')

// Post-publish board re-reads. MURKs or Twilio normally write a status inside
// 25s; five full reads over 32s cover that without hammering Knack's rate limit.
export const STATUS_POLL_DELAYS_MS = [2000, 2000, 4000, 8000, 16000]
export const STATUS_POLL_WINDOW_MS = STATUS_POLL_DELAYS_MS.reduce(
  (total, delay) => total + delay,
  0
)
// Auto-dismisses: there is no action to offer, so a persistent toast would
// only be ignored.
export const STATUSES_STILL_UPDATING_NOTICE = {
  type: 'warn',
  title: 'Shifts are still publishing',
  text: 'Statuses need more time to update.',
  duration: 8000,
}

export default {
  name: 'WithAssetsAndAssignments',
  props: {
    assetType: {
      type: String,
      required: false,
      default: '',
    },
    date: {
      type: String,
      default: () => dayjs().format('MM/DD/YYYY'),
    },
  },
  data() {
    return {
      isDataLoading: false,
    }
  },
  computed: {
    ...routeState(['query']),
    ...mapGetters([
      'getAvailableAssets',
      'getAssignedAssets',
      'getAllAssets',
      'getPendingStatusShiftIds',
    ]),
    computedAssetType() {
      return this.assetType || this.$route.params.assetType
    }, // computedAssetType
    allAssets() {
      return this.isDataLoading ? [] : this.getAllAssets(this.computedAssetType)
    }, // allAssets
    // full repsonse object including records and metadata too
    assignedAssets() {
      return this.isDataLoading
        ? []
        : this.getAssignedAssets(this.computedAssetType)
    },
    availableAssets() {
      return this.isDataLoading
        ? []
        : this.getAvailableAssets(this.computedAssetType)
    },
    filters() {
      const fields = HumanFields[this.computedAssetType.toUpperCase()]
      const rules = [
        { field: fields.STATUS, operator: 'is not', value: 'Inactive' },
      ]

      const exclusionRule = dispatcherExclusionRule(
        this.computedAssetType,
        fields
      )
      if (exclusionRule) rules.push(exclusionRule)

      return { match: 'and', rules }
    }, // filters
    // Poll only once the board is in: shifts persisted across a reload are
    // pending before the first read, and the initial reads must not race it.
    shouldPollStatuses() {
      return !this.isDataLoading && this.getPendingStatusShiftIds.length > 0
    }, // shouldPollStatuses
  },

  watch: {
    shouldPollStatuses: {
      immediate: true,
      handler(shouldPoll) {
        if (shouldPoll) {
          this.startStatusPolling()
        } else {
          this.stopStatusPolling()
        }
      },
    },
    // A later send restarts the schedule so its shifts get the full window.
    getPendingStatusShiftIds(shiftIds, previousShiftIds) {
      let joined = shiftIds.some((id) => !previousShiftIds.includes(id))
      if (joined && this.shouldPollStatuses) {
        this.startStatusPolling()
      }
    },
    date() {
      this.stopStatusPolling()
    },
    assetType: {
      async handler(val) {
        this.isDataLoading = true
        // First load all the assets
        await this.fetchAllAssets({
          assetType: this.computedAssetType,
          rows_per_page: 1000,
          filters: this.filters,
        })
        // Then load the assigned assets for this date/type
        await this.fetchAssignedAssets({
          assetType: this.computedAssetType,
          date: this.date,
          rows_per_page: 1000,
        })
        this.isDataLoading = false
      },
      immediate: true,
    },
  },
  // beforeCreate: the immediate shouldPollStatuses watcher runs before created
  // and needs the poller. Off data() on purpose; nothing renders from it.
  beforeCreate() {
    this.statusPoller = createStatusPoller({
      delays: STATUS_POLL_DELAYS_MS,
      read: () =>
        this.refreshAssignedAssets({
          assetType: this.computedAssetType,
          date: this.date,
          rows_per_page: 1000,
        }),
      isStillWaiting: () => this.getPendingStatusShiftIds.length > 0,
      // The success screen promised these would update.
      onGaveUp: () => {
        this.clearPendingStatusShifts()
        this.$notify(STATUSES_STILL_UPDATING_NOTICE)
      },
    })
  },
  beforeDestroy() {
    this.stopStatusPolling()
  },
  methods: {
    ...mapActions([
      'fetchAssignedAssets',
      'fetchAllAssets',
      'refreshAssignedAssets',
      'clearPendingStatusShifts',
    ]),
    // Both watchers above fire on the first send; the second start only
    // re-arms the same first delay.
    startStatusPolling() {
      this.statusPoller.start()
    }, // startStatusPolling
    stopStatusPolling() {
      this.statusPoller.stop()
    }, // stopStatusPolling
  },
  render() {
    const { isDataLoading, allAssets, availableAssets, assignedAssets } = this
    return this.$scopedSlots.default({
      isDataLoading,
      availableAssets,
      allAssets,
      assignedAssets,
    })
  },
}
</script>
